import { useState, useEffect } from 'react';
import { Table, Modal, Button } from 'antd';
import { EditOutlined, CloseCircleOutlined, CheckCircleOutlined, MinusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'common/services/currency';
import { ColumnProps } from 'antd/lib/table';

import { FondForm } from '../../forms';
import { FondDataFormValues, HazardProfileFormValues } from '../../models/Forms';

interface Props {
  columns: Array<ColumnProps<{ key: string | number }>>;
  fondTypeOptions: Array<{ label: string; value: string }>;
  onChange: (values: FondDataFormValues[]) => void;
  strategy?: { name: string; type: string };
  hazard?: HazardProfileFormValues | null;
  initialData?: FondDataFormValues[];
  currency?: string;
}

function FondTable({ initialData, onChange, strategy, hazard, columns, fondTypeOptions, currency }: Props) {
  const { t } = useTranslation();
  const [modalVisibility, setModalVisibility] = useState(false);
  const [data, setData] = useState<FondDataFormValues[]>(initialData || []);
  const [selectedRow, setSelectedRow] = useState<null | number>(null);

  /* eslint-disable */
  useEffect(() => {
    onChange && onChange(data);
  }, [data]);
  /* eslint-enable */

  function handleAddNew() {
    setModalVisibility(true);
  }

  function handleSubmit(values: FondDataFormValues) {
    if (selectedRow === null) {
      setData([...data, { ...values }]);
    } else {
      const newData = data;
      newData[selectedRow] = values;
      setData(newData);
      setSelectedRow(null);
    }
    setModalVisibility(false);
  }

  function handleClose() {
    setModalVisibility(false);
    setSelectedRow(null);
  }

  function handleEdit(index: number) {
    return () => {
      setSelectedRow(index);
      setModalVisibility(true);
    };
  }

  function handleDelete(index: number) {
    return () => {
      setData(data.filter((_e, i) => i !== index));
    };
  }

  const actionsColumn = [
    {
      key: 'action',
      render: (_v: any, _r: any, index: number) => (
        <span className="fond-table-actions">
          <EditOutlined onClick={handleEdit(index)} />
          <CloseCircleOutlined onClick={handleDelete(index)} />
        </span>
      ),
    },
  ];

  return (
    <>
      <Modal
        centered
        closable
        title={t('fondTable.title')}
        open={modalVisibility}
        footer={null}
        destroyOnHidden
        onCancel={handleClose}
      >
        <FondForm
          onSubmit={handleSubmit}
          strategy={strategy}
          hazard={hazard}
          initialValues={selectedRow === null ? undefined : data[selectedRow]}
          fondTypeOptions={fondTypeOptions}
          currency={currency}
        />
      </Modal>
      <Table
        className="fond-table"
        bordered
        columns={columns.concat(actionsColumn)}
        dataSource={data.map((d, index) => ({ ...d, key: index }))}
        pagination={false}
        footer={() => (
          <Button type="primary" onClick={handleAddNew}>
            {t('fondTable.add')}
          </Button>
        )}
      />
    </>
  );
}

type FondsTableProps = Omit<Props, 'columns' | 'fondTypeOptions'>;

export function NewFondsTable({ currency, ...tableProps }: FondsTableProps) {
  const { t } = useTranslation();

  return (
    <FondTable
      {...tableProps}
      currency={currency}
      columns={[
        {
          title: t('fondTable.columns.code'),
          dataIndex: 'code',
          key: 'code',
          align: 'left',
        },
        {
          title: t('fondTable.columns.buy'),
          dataIndex: 'type',
          key: 'buy',
          align: 'center',
          render: (value: string) => (value === 'buy' ? <CheckCircleOutlined /> : <MinusOutlined />),
        },
        {
          title: t('fondTable.columns.sell'),
          dataIndex: 'type',
          key: 'sell',
          align: 'center',
          render: (value: string) => (value === 'sell' ? <CheckCircleOutlined /> : <MinusOutlined />),
        },
        {
          title: t('fondTable.columns.hold'),
          dataIndex: 'type',
          key: 'hold',
          align: 'center',
          render: (value: string) => (value === 'hold' ? <CheckCircleOutlined /> : <MinusOutlined />),
        },
        {
          title: `${t('fondTable.columns.regularAmount')} ${currency}`,
          dataIndex: 'regularAmount',
          key: 'regularAmount',
          align: 'center',
          render: (value: any) => formatCurrency(value, {}),
        },
        {
          title: `${t('fondTable.columns.oneTimeAmount')} ${currency}`,
          dataIndex: 'oneTimeAmount',
          key: 'oneTimeAmount',
          align: 'center',
          render: (value: any) => formatCurrency(value),
        },
        {
          title: t('fondTable.columns.agio'),
          dataIndex: 'agio',
          key: 'agio',
          align: 'center',
        },
        {
          title: t('fondTable.columns.note'),
          dataIndex: 'note',
          key: 'note',
          align: 'right',
        },
      ]}
      fondTypeOptions={[
        {
          label: t('fondTable.columns.buy'),
          value: 'buy',
        },
        {
          label: t('fondTable.columns.sell'),
          value: 'sell',
        },
        {
          label: t('fondTable.columns.hold'),
          value: 'hold',
        },
      ]}
    />
  );
}

export function HeldFondsTable({ currency, ...tableProps }: FondsTableProps) {
  const { t } = useTranslation();

  return (
    <FondTable
      {...tableProps}
      currency={currency}
      columns={[
        {
          title: t('fondTable.columns.code'),
          dataIndex: 'code',
          key: 'code',
          align: 'left',
        },
        {
          title: t('fondTable.columns.sell'),
          dataIndex: 'type',
          key: 'sell',
          align: 'center',
          render: (value: string) => (value === 'sell' ? <CheckCircleOutlined /> : <MinusOutlined />),
        },
        {
          title: t('fondTable.columns.hold'),
          dataIndex: 'type',
          key: 'hold',
          align: 'center',
          render: (value: string) => (value === 'hold' ? <CheckCircleOutlined /> : <MinusOutlined />),
        },
        {
          title: `${t('fondTable.columns.regularAmount')} ${currency}`,
          dataIndex: 'regularAmount',
          key: 'regularAmount',
          align: 'center',
          render: (value: any) => formatCurrency(value, {}),
        },
        {
          title: `${t('fondTable.columns.oneTimeAmount')} ${currency}`,
          dataIndex: 'oneTimeAmount',
          key: 'oneTimeAmount',
          align: 'center',
          render: (value: any) => formatCurrency(value),
        },
        {
          title: t('fondTable.columns.agio'),
          dataIndex: 'agio',
          key: 'agio',
          align: 'center',
        },
        {
          title: t('fondTable.columns.note'),
          dataIndex: 'note',
          key: 'note',
          align: 'right',
        },
      ]}
      fondTypeOptions={[
        {
          label: t('fondTable.columns.sell'),
          value: 'sell',
        },
        {
          label: t('fondTable.columns.hold'),
          value: 'hold',
        },
      ]}
    />
  );
}
